import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { artistAdminAPI, resolveMediaSrc } from '@/lib/api';
import { BrutalCard, BrutalButton, BrutalInput, BrutalBadge } from '@/components/brutal';
import { Music, Users, RefreshCw, Play, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const apiError = (e) => e.response?.data?.detail || 'Something went wrong';

const ArtistsCatalogTab = () => {
  const qc = useQueryClient();
  const [form, setForm] = useState({ stage_name: '', bio: '', country: '' });
  const [sync, setSync] = useState({ count: 5, approve: false });
  const [filter, setFilter] = useState('');
  const [assignTo, setAssignTo] = useState({});

  const artists = useQuery({ queryKey: ['admin-artists'], queryFn: async () => (await artistAdminAPI.listArtists()).data });
  const logins = useQuery({ queryKey: ['admin-artist-logins'], queryFn: async () => (await artistAdminAPI.listLogins()).data });
  const songs = useQuery({ queryKey: ['catalog-songs'], queryFn: async () => (await artistAdminAPI.listSongs()).data });
  const audit = useQuery({ queryKey: ['catalog-audit'], queryFn: async () => (await artistAdminAPI.audit()).data });

  const refresh = () => ['admin-artists', 'admin-artist-logins', 'catalog-songs', 'catalog-audit'].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const run = (fn, ok) => ({ mutationFn: fn, onSuccess: (r) => { toast.success(typeof ok === 'function' ? ok(r) : ok); refresh(); }, onError: (e) => toast.error(apiError(e)) });

  const create = useMutation(run(() => artistAdminAPI.createArtist({ ...form, genres: [], website: '', status: 'approved' }), 'Artist profile created'));
  const artistStatus = useMutation(run(({ id, status }) => artistAdminAPI.setStatus(id, status), 'Artist updated'));
  const assign = useMutation(run(({ id, userId }) => artistAdminAPI.assign(id, userId), 'Profile given to that login'));
  const songStatus = useMutation(run(({ id, status }) => artistAdminAPI.setSongStatus(id, status), 'Song updated'));
  const songAccess = useMutation(run(({ id, plan, minAge, ask }) => artistAdminAPI.setSongAccess(id, plan, minAge, ask), 'Access updated'));
  // Withdrawing shows how many stories it touches first, asks for a reason, then swaps or removes it everywhere.
  const withdraw = async (kind, id, label) => {
    try {
      const { data: pre } = await artistAdminAPI.takedownPreview(kind, id);
      const reach = kind === 'artist' ? `${pre.songs} songs in ${pre.stories} stories` : `${pre.stories} stories${pre.purchased_downloads ? ` and ${pre.purchased_downloads} purchased downloads` : ''}`;
      if (!window.confirm(`Withdraw ${label}? It is used in ${reach}. Each story gets a similar replacement, or the mention is taken out.`)) return;
      const reason = window.prompt('Reason for withdrawing (kept in the record):');
      if (!reason || reason.trim().length < 3) { toast.error('A reason is required'); return; }
      const { data: r } = await artistAdminAPI.takedown(kind, id, reason.trim());
      toast.success(`Withdrawn. ${r.replaced || 0} replaced, ${r.removed || 0} taken out${r.downloads_to_review ? `, ${r.downloads_to_review} purchased downloads to review` : ''}`);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doSync = useMutation(run(() => artistAdminAPI.sync(sync), (r) => `Synced ${r.data.synced.length} songs${r.data.failed.length ? `, ${r.data.failed.length} failed` : ''}`));

  const play = async (id) => {
    try {
      const src = await resolveMediaSrc(`/api/media/gridfs/${id}`);
      new Audio(src).play();
    } catch (e) { toast.error('Could not play that song'); }
  };

  const artistList = artists.data || [];
  const unclaimed = artistList.filter((a) => !a.user_id);
  const songList = (songs.data || []).filter((s) => !filter || `${s.title} ${s.country} ${s.genre}`.toLowerCase().includes(filter.toLowerCase()));
  const pendingCount = (songs.data || []).filter((s) => s.status !== 'approved').length;

  return (
    <div className="space-y-6" data-testid="artists-catalog-tab">
      <BrutalCard>
        <h3 className="text-lg font-bold flex items-center gap-2 mb-3"><Users size={18} /> Artists ({artistList.length})</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left border-b-2 border-black"><th className="py-2 pr-3">Stage name</th><th className="pr-3">Login</th><th className="pr-3">Songs</th><th className="pr-3">Status</th><th>Actions</th></tr></thead>
            <tbody>
              {artistList.map((a) => (
                <tr key={a.id} className="border-b border-gray-200 align-top">
                  <td className="py-2 pr-3 font-semibold">{a.stage_name}</td>
                  <td className="pr-3">
                    {a.user_id ? 'Has a login' : (
                      <span className="flex gap-1 flex-wrap">
                        <select className="border-2 border-black px-1 text-xs" value={assignTo[a.id] || ''} onChange={(e) => setAssignTo({ ...assignTo, [a.id]: e.target.value })}>
                          <option value="">Unclaimed: give to…</option>
                          {(logins.data || []).map((u) => <option key={u.id} value={u.id}>{u.email}</option>)}
                        </select>
                        <BrutalButton size="sm" disabled={!assignTo[a.id]} onClick={() => assign.mutate({ id: a.id, userId: assignTo[a.id] })}>Give</BrutalButton>
                      </span>
                    )}
                  </td>
                  <td className="pr-3">{a.song_count}</td>
                  <td className="pr-3"><BrutalBadge variant={a.status === 'approved' ? 'emerald' : 'amber'}>{a.status}</BrutalBadge></td>
                  <td>
                    {a.status !== 'withdrawn' && (
                      <span className="flex gap-1 flex-wrap">
                        <BrutalButton size="sm" onClick={() => artistStatus.mutate({ id: a.id, status: a.status === 'approved' ? 'pending' : 'approved' })}>
                          {a.status === 'approved' ? 'Hide from children' : 'Approve'}
                        </BrutalButton>
                        <BrutalButton size="sm" variant="rose" onClick={() => withdraw('artist', a.id, a.stage_name)}>Withdraw</BrutalButton>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {artistList.length === 0 && <tr><td colSpan={5} className="py-3 text-gray-600">No artists yet.</td></tr>}
            </tbody>
          </table>
        </div>
        {unclaimed.length > 0 && <p className="text-xs text-gray-600 mt-2">Unclaimed profiles belong to the label. An artist can only take one over when you give it to their login.</p>}
        <form className="grid gap-2 sm:grid-cols-4 mt-4" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
          <BrutalInput placeholder="New artist stage name" required value={form.stage_name} onChange={(e) => setForm({ ...form, stage_name: e.target.value })} />
          <BrutalInput placeholder="Country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
          <BrutalInput placeholder="Short bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
          <BrutalButton type="submit" variant="indigo" disabled={create.isPending}>Create profile</BrutalButton>
        </form>
      </BrutalCard>

      <BrutalCard>
        <h3 className="text-lg font-bold flex items-center gap-2 mb-1"><RefreshCw size={18} /> Songs For Centuries sync</h3>
        <p className="text-sm text-gray-700 mb-3">Copies songs from songsforcenturies.com into our storage. New songs stay hidden from children until approved.</p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">How many (1–10)
            <input type="number" min={1} max={10} value={sync.count} onChange={(e) => setSync({ ...sync, count: Math.max(1, Math.min(10, Number(e.target.value) || 1)) })} className="block border-2 border-black px-2 py-1 w-24" />
          </label>
          <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={sync.approve} onChange={(e) => setSync({ ...sync, approve: e.target.checked })} /> Approve straight away</label>
          <BrutalButton variant="indigo" disabled={doSync.isPending} onClick={() => doSync.mutate()} data-testid="sfc-sync-btn">{doSync.isPending ? 'Syncing… (can take a minute)' : 'Sync random songs'}</BrutalButton>
        </div>
      </BrutalCard>

      <BrutalCard>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h3 className="text-lg font-bold flex items-center gap-2"><Music size={18} /> Catalogue ({(songs.data || []).length}, {pendingCount} waiting for approval)</h3>
          <BrutalInput placeholder="Search title, country, genre" value={filter} onChange={(e) => setFilter(e.target.value)} className="max-w-xs" />
        </div>
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left border-b-2 border-black"><th className="py-2 pr-3">Title</th><th className="pr-3">Country</th><th className="pr-3">Status</th><th className="pr-3">Who can play</th><th>Actions</th></tr></thead>
            <tbody>
              {songList.map((s) => (
                <tr key={s.id} className="border-b border-gray-200">
                  <td className="py-2 pr-3">{s.title}</td>
                  <td className="pr-3">{s.country}</td>
                  <td className="pr-3"><BrutalBadge variant={s.status === 'approved' ? 'emerald' : 'amber'}>{s.status}</BrutalBadge></td>
                  <td className="pr-3">
                    <span className="flex gap-1 flex-wrap items-center text-xs">
                      <select aria-label="Plan needed" className="border-2 border-black px-1" value={s.required_plan || ''} onChange={(e) => songAccess.mutate({ id: s.id, plan: e.target.value, minAge: s.min_age || 0, ask: !!s.requires_permission })}>
                        <option value="">Any plan</option>
                        {['starter', 'family', 'pro'].map((p) => <option key={p} value={p}>{p}+</option>)}
                      </select>
                      <select aria-label="Best for ages" className="border-2 border-black px-1" value={s.min_age || 0} onChange={(e) => songAccess.mutate({ id: s.id, plan: s.required_plan || '', minAge: Number(e.target.value), ask: !!s.requires_permission })}>
                        <option value={0}>All ages</option>
                        {[6, 8, 10, 12, 14].map((a) => <option key={a} value={a}>best for {a}+</option>)}
                      </select>
                      <label className="flex items-center gap-1"><input type="checkbox" checked={!!s.requires_permission} onChange={(e) => songAccess.mutate({ id: s.id, plan: s.required_plan || '', minAge: s.min_age || 0, ask: e.target.checked })} />parent OK</label>
                    </span>
                  </td>
                  <td className="flex gap-1">
                    <BrutalButton size="sm" onClick={() => play(s.id)} aria-label="Play"><Play size={12} /></BrutalButton>
                    {s.status !== 'withdrawn' && (
                      <>
                        <BrutalButton size="sm" onClick={() => songStatus.mutate({ id: s.id, status: s.status === 'approved' ? 'pending' : 'approved' })}>
                          {s.status === 'approved' ? 'Hide' : 'Approve'}
                        </BrutalButton>
                        <BrutalButton size="sm" variant="rose" onClick={() => withdraw('song', s.id, s.title)}>Withdraw</BrutalButton>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {songList.length === 0 && <tr><td colSpan={5} className="py-3 text-gray-600">No songs yet. Use the sync above.</td></tr>}
            </tbody>
          </table>
        </div>
      </BrutalCard>

      <BrutalCard>
        <h3 className="text-lg font-bold flex items-center gap-2 mb-3"><ShieldCheck size={18} /> Catalogue access log</h3>
        <ul className="text-sm space-y-1 max-h-48 overflow-y-auto">
          {(audit.data || []).slice(0, 25).map((e) => (
            <li key={e.id} className="font-mono text-xs">{new Date(e.at).toLocaleString()} · {e.via} · {e.action}{e.isrc ? ` · ${e.isrc}` : ''}{e.synced !== undefined ? ` · ${e.synced}/${e.requested} songs` : ''}</li>
          ))}
          {(audit.data || []).length === 0 && <li className="text-gray-600">Nothing yet.</li>}
        </ul>
      </BrutalCard>
    </div>
  );
};

export default ArtistsCatalogTab;
